import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import useStarfishAPI from 'hooks/useStarfishAPI';
import { isEmptyString } from 'utils/text';
import StyledButton from 'common/StyledButton';
import css from './HelpDeskInfoModal.scss';
import Icon from 'SF/MaterialIcon';
import HelpDeskInfo from './HelpDeskInfo';

/**
 * @param {boolean} allowRedirect - if redirects to the Starfish Help Center should be allowed
 *                                  (ex., we should never automatically redirect from the profile)
 * @param {boolean} isOpen - if the modal is open
 * @param {function} onClose - closes the modal
 * @param {boolean} showStarfishHelpLink - if the user should see the Starfish Help Center link
 *                                         (ex., students should never be able to see or be redirected to the
 *                                         Starfish Help Center, which is only written for faculty users)
 * @returns {JSX.Element|string|null} Opens a modal displaying Help Desk contact information for texting
 */
function HelpDeskInfoModal({ allowRedirect, isOpen, onClose, showStarfishHelpLink }) {
    Modal.setAppElement(document.getElementById("main-center"));

    const [helpDeskInformation, callHelpDeskInformationAPI] = useStarfishAPI();

    useEffect(() => {
        callHelpDeskInformationAPI('../help-desk-information.html', { method: 'GET' });
    }, []);

    const emptyHelpDeskInfo = () =>
        helpDeskInformation.success
        && isEmptyString(helpDeskInformation.data.url)
        && isEmptyString(helpDeskInformation.data.phone)
        && isEmptyString(helpDeskInformation.data.email);

    const content = () =>
        emptyHelpDeskInfo() ? (
            <p className={css['empty-text']}>
                {
                    "Your institution has not provided this information yet. " +
                    "Please visit your institution's website for technical support."
                }
            </p>
        ) : (
            <HelpDeskInfo helpDeskInformation={helpDeskInformation.data}
                          showStarfishHelpLink={showStarfishHelpLink} />
        );

    if (!helpDeskInformation.success) return "Loading...";
    if (allowRedirect && showStarfishHelpLink
        && (!helpDeskInformation.data.showToStaff || emptyHelpDeskInfo())) {
        window.open('../support/help.html', '_blank');
        return null;
    }
    return <Modal className={css['modal']}
                  onConfirm={onClose}
                  onRequestClose={onClose}
                  isOpen={isOpen}
                  overlayClassName={css['overlay']}>
        <header>
            <StyledButton
                className={css['close-button']}
                onClick={onClose}
                aria-label="Close"
                asLink>
                <Icon icon='close' />
            </StyledButton>
        </header>
        <div className={css['content']}>
            {content()}
            <div className={css['actions']}>
                <StyledButton onClick={onClose} data-role="confirmSubmit">Ok, thanks!</StyledButton>
            </div>
        </div>
    </Modal>
}

HelpDeskInfoModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    showStarfishHelpLink: PropTypes.bool,
    allowRedirect: PropTypes.bool
}

export default HelpDeskInfoModal;
